import { Card, CardContent } from '@/components/ui/card';
import { Users, Briefcase, Building2 } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useTypingAnimation } from '@/hooks/use-typing-animation';

const AboutSection = () => {
  const { ref: heading1Ref, displayedText: heading1Text } = useTypingAnimation('Key Achievements', 60);
  const { ref: heading2Ref, displayedText: heading2Text } = useTypingAnimation('Digital Marketing', 60);
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
        }
      },
      {
        threshold: 0.3,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const stats = [
    {
      icon: Briefcase,
      value: '10+',
      label: 'Projects Completed',
    },
    {
      icon: Building2,
      value: '5+',
      label: 'Companies Worked With',
    },
    {
      icon: Users,
      value: '10+',
      label: 'Happy Clients',
    },
  ];
  return (
    <section id="about" className="py-24 bg-gradient-to-b from-background to-secondary/30" ref={sectionRef}>
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Stats Cards */}
            <div className="space-y-6">
              <div className="space-y-2 mb-8">
                <p className="text-primary font-semibold uppercase tracking-wider text-sm">
                  My Journey
                </p>
                <h3 ref={heading1Ref as any} className="text-3xl font-bold min-h-[2.5rem]">
                  {heading1Text}
                </h3>
              </div>
              <div className="grid gap-5">
                {stats.map((stat, index) => (
                  <StatCard key={index} stat={stat} index={index} hasAnimated={hasAnimated} />
                ))}
              </div>
            </div>

            {/* About Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <p className="text-primary font-semibold uppercase tracking-wider text-sm">
                  About Rakib Hossen Topu
                </p>
                <h2 ref={heading2Ref as any} className="text-4xl md:text-5xl font-bold leading-tight min-h-[3.5rem]">
                  {heading2Text} <span className="gradient-text">Expert</span>
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  I'm <strong>Rakib Hossen Topu</strong>, a professional <strong>SEO Expert in BD</strong> with over 5 years of experience helping businesses grow online. As a leading digital marketing specialist in Bangladesh, I specialize in Local SEO, E-commerce SEO, YouTube SEO, and comprehensive digital marketing strategies.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Each project showcases my dedication to detail, creativity, and excellence. As one of the top SEO experts in Bangladesh, I believe in creating data-driven solutions that not only improve search rankings but also provide real value to users and businesses across BD and worldwide.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatCard = ({
  stat,
  index,
  hasAnimated,
}: {
  stat: {
    icon: any;
    value: string;
    label: string;
  };
  index: number;
  hasAnimated: boolean;
}) => {
  const [count, setCount] = useState(0);
  const targetNumber = parseInt(stat.value);
  const suffix = stat.value.replace(/[0-9]/g, '');

  useEffect(() => {
    if (!hasAnimated) return;

    let startTime: number;
    const duration = 2000;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * targetNumber));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [hasAnimated, targetNumber]);

  const Icon = stat.icon;
  
  return (
    <Card 
      className="p-6 card-hover bg-card border-border/50 backdrop-blur-sm"
      style={{
        animation: hasAnimated ? `fade-in 0.5s ease-out ${index * 0.15}s both` : 'none'
      }}
    >
      <CardContent className="p-0 flex items-center gap-5">
        <div className="p-4 rounded-2xl bg-gradient-to-br from-primary/10 to-primary-glow/5 border border-primary/20">
          <Icon className="w-8 h-8 text-primary" />
        </div>
        <div>
          <div className="text-4xl font-bold gradient-text mb-1">
            {count}{suffix}
          </div>
          <p className="text-muted-foreground text-sm font-medium">{stat.label}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default AboutSection;
